import { defineStore } from 'pinia'
import { findCategory } from '~/utils/shop'
import type { ContentAPIResponse } from '~/types/content'
import { getLanguagePath } from '~/utils'
import { isSuspectContentPath } from '~/utils/cmsLinks'
import { useLocaleStore } from '~/stores/locale'
import { useAgentStore } from '~/stores/agent'
import { useRootStore } from '~/stores/root'

export const useNavigationStore = defineStore('navigation', () => {
  const localeStore = useLocaleStore()
  const agentStore = useAgentStore()
  const rootStore = useRootStore()
  const route = useRoute()

  const categoryBySlug = (slug: string) => {
    for (const category of rootStore.categories) {
      const result = findCategory(category, slug)
      if (result) {
        return result
      }
    }
  }

  const pageData = ref<{[id: string]: ContentAPIResponse}>({})

  const pageDataByKey = (contentKey: string): ContentAPIResponse | false => {
    const languagePath = getLanguagePath(localeStore.culture)
    const key = languagePath ? contentKey.replace(languagePath + '/', '/') : contentKey
    return pageData.value[key] ?? false
  }

  const currentCategorySlug = computed(() => {
    // Match the nav tree's full-path slugs (e.g. /shop-products/kits-collection).
    // route.params.slug only captures the catch-all tail ("kits-collection"), which
    // never matches the tree — so use the full route path with the sub-locale
    // language prefix (e.g. /fr) stripped.
    const languagePath = getLanguagePath(localeStore.culture)
    return languagePath ? route.path.replace(languagePath + '/', '/') : route.path
  })

  // Lags a client navigation between page components. `route` is Nuxt's global
  // route, which only syncs once the new page has resolved (page:finish), after the
  // new page's setup and onMounted have run; until then this is the PREVIOUS page's
  // category. A page that needs its own category during setup resolves it from its
  // own useRoute() with categoryBySlug, as ShopProductsPage does (AB#64581).
  const currentCategory = computed(() => categoryBySlug(currentCategorySlug.value))

  const fetchHtmlContent = async (path: string) => {
    // remove the language portion (ie ~/footer/fr/help/faqs) since content path does not include langauge portion
    // needs the trailing slash found a bug where it removed the /fr in specials/free-download
    const languagePath = getLanguagePath(localeStore.culture)
    const actualPath = languagePath ? path.replace(languagePath + '/', '/') : path
    const contentPath = actualPath.split('#').shift() ?? ''

    // A malformed, link-derived path (unescaped href fragment, email, null segment)
    // is never a real content route. Skip the lookup — it would 404 and log a Critical
    // on the backend for every hit. The upstream fix is toInternalPath() in CmsContent.
    if (isSuspectContentPath(contentPath)) {
      return undefined
    }

    const contentAPIResponse: ContentAPIResponse = await $api()(
      'content/',
      {
        params: {
          path: `~/${contentPath}`,
          // Match nuxt2: only send viewAsDate when set. agentStore.viewAsDate is
          // null for normal users, and ofetch serializes null as an empty
          // `?viewAsDate=` param (it omits only undefined) — so coalesce to undefined.
          viewAsDate: agentStore.viewAsDate || undefined
        }
      }
    )
    if (contentAPIResponse) {
      const matchesAddToCart = contentAPIResponse.content.match(/(data-add-to-cart="\d+")/g) as RegExpMatchArray
      if (matchesAddToCart?.length > 0) {
        matchesAddToCart.forEach((match) => {
          const productId = match.match(/(\d+)/)?.[0]
          if (productId === undefined) {
            throw new Error(`Can't find productId for ~/${contentPath} content for match "${match}"`)
          }
          contentAPIResponse.content = contentAPIResponse.content.replace(match,
            `onclick="window.useEvent('add-product-by-product-id', {culture:'${localeStore.culture}',productId:${productId},qty:1})"`)
        })
      }
      const matchesEmit = contentAPIResponse.content.match(/(data-emit=\\?".*?")/g) as RegExpMatchArray
      if (matchesEmit?.length > 0) {
        matchesEmit.forEach((match) => {
          const event = match.match(/"(.+?(?=\\?"))/)?.[1]
          const escapedCharacter = (/\\/).test(match) ? '\\' : ''
          if (event === undefined) {
            throw new Error(`Can't find event for ~/${contentPath} content for match "${match}"`)
          }
          contentAPIResponse.content = contentAPIResponse.content.replace(match,
            `onclick=${escapedCharacter}"window.useEvent('${event}',null)${escapedCharacter}"`)
        })
      }
    }
    pageData.value[contentPath] = contentAPIResponse
    return contentAPIResponse
  }

  return {
    pageData,
    categoryBySlug,
    fetchHtmlContent,
    pageDataByKey,
    currentCategory,
    currentCategorySlug
  }
})
